import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Title,
  Text,
  Badge,
  Group,
  Button,
  Table,
  Drawer,
  Stack,
  Tabs,
  LoadingOverlay,
  TextInput,
  Modal,
  Textarea,
  Divider,
  Center,
  Pagination,
  Anchor,
} from "@mantine/core";
import { IconSearch, IconEye } from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";


// Applicants type social links freely, so stored values are inconsistent: some are
// full URLs, some are bare domains like "tiktok.com/@user" with no scheme, and many
// are just handles ("hazakura.cos") or junk ("tik tok").
//
// A schemeless value in href is resolved RELATIVE to the current admin page, which is
// why "tiktok.com/@zedgrammy" navigated to /admin/dashboard/tiktok.com/@zedgrammy
// instead of TikTok. Only linkify values we can resolve with confidence; anything
// else renders as plain text rather than a link that goes nowhere useful.
const toExternalHref = (value) => {
  const v = String(value ?? "").trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  // domain + path, e.g. "tiktok.com/@zedgrammy" -> needs a scheme prepended
  if (/^[\w-]+(\.[\w-]+)+\/\S*$/.test(v)) return `https://${v}`;
  return null;
};

const ADMIN_API = `${API_BASE_URL}/`;
const getAdminHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const STATUS_COLOR = { PENDING: "orange", APPROVED: "green", REJECTED: "red" };

export default function CreatorApplicationsReview() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("PENDING");
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });

  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const { showSuccess, showError } = useNotification();

  const fetchApplications = async (page = 1) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        status: activeTab,
        page,
        limit: 10,
        ...(search ? { search } : {}),
      });
      const res = await fetch(`${ADMIN_API}creator-applications?${query}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        setApplications(data.data.applications);
        setPagination({
          page: data.data.pagination.page,
          totalPages: data.data.pagination.totalPages,
        });
      } else {
        showError("Error", data.error?.message || "Failed to load applications.");
      }
    } catch (e) {
      showError("Error", "Network error while loading applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const openReview = (app) => {
    setSelectedApp(app);
    setReviewOpen(true);
  };

  const closeAll = () => {
    setReviewOpen(false);
    setRejectOpen(false);
    setSelectedApp(null);
    setReviewNote("");
  };

  const handleApprove = async () => {
    if (!selectedApp) return;
    setActionLoading(true);
    try {
      const res = await fetch(
        `${ADMIN_API}creator-applications/${selectedApp.id}/approve`,
        { method: "POST", headers: getAdminHeaders() },
      );
      const data = await res.json();
      if (data.success) {
        showSuccess("Approved", `${selectedApp.creatorName} is now a Creator.`);
        closeAll();
        fetchApplications(pagination.page);
      } else {
        showError("Approval failed", data.error?.message || "Something went wrong.");
      }
    } catch (e) {
      showError("Error", "Network error during approval.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApp) return;
    setActionLoading(true);
    try {
      const res = await fetch(
        `${ADMIN_API}creator-applications/${selectedApp.id}/reject`,
        {
          method: "POST",
          headers: getAdminHeaders(),
          body: JSON.stringify({ reviewNote: reviewNote || undefined }),
        },
      );
      const data = await res.json();
      if (data.success) {
        showSuccess("Rejected", "The application has been declined.");
        closeAll();
        fetchApplications(pagination.page);
      } else {
        showError("Rejection failed", data.error?.message || "Something went wrong.");
      }
    } catch (e) {
      showError("Error", "Network error during rejection.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box className="space-y-6">
      <Group justify="space-between">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            Creator Applications
          </Text>
          <Text size="sm" c="dimmed">
            Review "Become a Creator" applications — approving unlocks self-serve
            Creator tier communities for the applicant.
          </Text>
        </Box>
      </Group>

      <Paper p="sm" radius="md" withBorder>
        <Group grow>
          <TextInput
            placeholder="Search by creator name, nickname, or email..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchApplications(1)}
          />
          <Button variant="light" onClick={() => fetchApplications(1)} w={100}>
            Search
          </Button>
        </Group>
      </Paper>

      <Paper withBorder radius="lg" shadow="sm" pos="relative">
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

        <Tabs value={activeTab} onChange={setActiveTab} color="blue" radius="md">
          <Tabs.List px="md" pt="md">
            <Tabs.Tab value="PENDING">
              <Text size="md" fw={600}>Pending</Text>
            </Tabs.Tab>
            <Tabs.Tab value="APPROVED">
              <Text size="md" fw={600}>Approved</Text>
            </Tabs.Tab>
            <Tabs.Tab value="REJECTED">
              <Text size="md" fw={600}>Rejected</Text>
            </Tabs.Tab>
          </Tabs.List>

          <Box p="md" mih={400}>
            <Table verticalSpacing="md" horizontalSpacing="md" highlightOnHover>
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Creator Name</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Applicant</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Category</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Submitted</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Status</Text></Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {applications.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6} ta="center" c="dimmed" py="xl">
                      <Text size="lg">No applications in this queue.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  applications.map((app) => (
                    <Table.Tr key={app.id}>
                      <Table.Td><Text fw={700} size="md">{app.creatorName}</Text></Table.Td>
                      <Table.Td>
                        <Text size="sm">{app.applicant?.nickname || "Unknown"}</Text>
                        <Text size="xs" c="dimmed">{app.applicant?.email}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color="gray" variant="light" size="sm">{app.contentCategory}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{new Date(app.createdAt).toLocaleDateString()}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={STATUS_COLOR[app.status]} variant="light" size="md">
                          {app.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          variant="light"
                          size="sm"
                          leftSection={<IconEye size={16} />}
                          onClick={() => openReview(app)}
                        >
                          Review
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Box>
        </Tabs>

        <Center p="md" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
          <Pagination
            total={pagination.totalPages}
            value={pagination.page}
            onChange={(p) => fetchApplications(p)}
          />
        </Center>
      </Paper>

      {/* REVIEW DRAWER — renders from the row already in local state, no detail fetch */}
      <Drawer
        opened={reviewOpen}
        onClose={closeAll}
        position="right"
        size="lg"
        title={<Title order={3} size="h2">Creator Application</Title>}
        padding="xl"
        overlayProps={{ opacity: 0.5, blur: 4 }}
      >
        {selectedApp && (
          <Stack gap="xl">
            <Paper withBorder p="lg" radius="md" bg="gray.0">
              <Text size="sm" tt="uppercase" c="dimmed" fw={700} mb="sm">
                Creator Profile
              </Text>
              <Title order={3} size="h2">{selectedApp.creatorName}</Title>
              <Badge variant="dot" color="blue" size="md" mt={4} mb="sm">
                {selectedApp.contentCategory}
              </Badge>
              <Text size="sm" c="dark.7">
                {selectedApp.bio || "No bio provided."}
              </Text>
            </Paper>

            <Box>
              <Title order={4} size="h3" mb="sm">Applicant</Title>
              <Paper withBorder p="lg" radius="md">
                <Text fw={600}>{selectedApp.applicant?.nickname || "Unknown"}</Text>
                <Text size="sm" c="dimmed">{selectedApp.applicant?.email}</Text>
              </Paper>
            </Box>

            <Box>
              <Title order={4} size="h3" mb="sm">Social Links</Title>
              <Paper withBorder p="lg" radius="md">
                {selectedApp.socialLinks && Object.keys(selectedApp.socialLinks).length > 0 ? (
                  <Stack gap={4}>
                    {Object.entries(selectedApp.socialLinks).map(([platform, url]) => {
                      const href = toExternalHref(url);
                      return (
                        <Group key={platform} gap="xs">
                          <Text size="sm" tt="capitalize" fw={600} w={90}>{platform}</Text>
                          {href ? (
                            <Anchor href={href} target="_blank" rel="noopener noreferrer" size="sm">{url}</Anchor>
                          ) : (
                            <Text size="sm" c="dimmed">{url}</Text>
                          )}
                        </Group>
                      );
                    })}
                  </Stack>
                ) : (
                  <Text size="sm" c="dimmed">No social links provided.</Text>
                )}
              </Paper>
            </Box>

            {selectedApp.status !== "PENDING" && (
              <Box>
                <Title order={4} size="h3" mb="sm">Review</Title>
                <Paper withBorder p="lg" radius="md">
                  <Text size="sm">
                    <Text span fw={600}>Reviewed by:</Text>{" "}
                    {selectedApp.reviewedBy?.nickname || "—"}
                  </Text>
                  {selectedApp.reviewNote && (
                    <Text size="sm" mt={4}>
                      <Text span fw={600}>Note:</Text> {selectedApp.reviewNote}
                    </Text>
                  )}
                </Paper>
              </Box>
            )}

            {selectedApp.status === "PENDING" && (
              <Box mt="auto" pt="xl" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
                <Group grow>
                  <Button
                    size="xl"
                    variant="light"
                    color="red"
                    disabled={actionLoading}
                    onClick={() => setRejectOpen(true)}
                  >
                    Reject
                  </Button>
                  <Button size="xl" color="green" loading={actionLoading} onClick={handleApprove}>
                    Approve
                  </Button>
                </Group>
              </Box>
            )}
          </Stack>
        )}
      </Drawer>

      {/* REJECT MODAL — collects a reviewNote; the backend actually persists this
          (the legacy StallApplication reject flow accepts a reason and silently
          drops it — this one is fixed, so the UI has to actually populate it). */}
      <Modal
        opened={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title={<Text fw={700}>Reject application</Text>}
      >
        <Stack>
          <Text size="sm" c="dimmed">
            Optional — this note is visible to the applicant.
          </Text>
          <Textarea
            placeholder="e.g. Add more social proof and reapply."
            minRows={3}
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
          />
          <Divider />
          <Group grow>
            <Button variant="default" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button color="red" loading={actionLoading} onClick={handleReject}>
              Confirm Rejection
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
